// GoodPlaceMatch - Main Component with Airbnb-Style UI

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import type {
  Place,
  PlaceFilter,
  PlaceMatchView,
  GoodPlaceMatchProps,
  PlaceCreateInput,
  MainCategory,
  GoodWorkCategory,
  AnyCategory,
  VibeTag,
  GoodWorkVibeTag,
  GeocodedLocation,
} from './types';
import { INITIAL_FILTERS } from './types';

// Services
import { searchPlaces, createPlace, subscribeToPlaces } from './services/placesService';
import { getUserFavorites, toggleFavorite, subscribeToFavorites } from './services/favoritesService';
import { isUserModerator, getPendingPlacesCount } from './services/moderationService';
import { isUserAdmin } from './services/adminService';
import { hasUserFeedbackForPlace, saveValouFeedback } from './services/valouFeedbackService';

// Context
import { useAuth, AuthProvider } from './context/AuthContext';
import { ConfigProvider, GoodPlaceConfig } from './context/ConfigContext';
import { RealmProvider, useRealm } from './context/RealmContext';

// Components - Core (always needed)
import { Header } from './components/common/Header';
import { CategoryBar } from './components/common/CategoryBar';
import { Footer } from './components/common/Footer';
import { LoadingScreen, LoadingSpinner } from './components/common/LoadingSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { FilterPanel } from './components/filters/FilterPanel';
import { PlaceGrid } from './components/places/PlaceGrid';
import { PlaceWizardIntro } from './components/places/PlaceWizardIntro';
import { FavoritesList } from './components/favorites/FavoritesList';
import { AuthModal } from './components/auth';
import { ToastContainer, useToast } from './components/common/Toast';
import { WelcomeScreen, useWelcomeScreen, OnboardingBanner } from './components/onboarding';
import { FeaturedContent, MagazinePage, ArticleDetail } from './components/content';
import type { Article } from './services/articlesService';
import { InspirationSection } from './components/inspiration';
import { MoodSelector, MoodType, QuickFilters, QuickFilterType, getFilterCriteria, MobileSearchModal, VibeTagFilter } from './components/discovery';
import { HeroSection, HeroMoodType, CuratedHomepage } from './components/home';
import { UserSearch, PublicProfile } from './components/users';
import { PrivacySettings } from './components/settings';
import { ConversationList, ChatView } from './components/messaging';
import { ConciergeButton, ConciergeBanner, ConciergeChat } from './components/concierge';
import { GroupsExplore, GroupDetail, GroupCreate, MyGroups } from './components/groups';
import { CommunityHub, CommunityEvents } from './components/community';
import { getOrCreateConversation } from './services/messagingService';
import { useUserLocation } from './hooks';
import { isPlaceOpenNow } from './utils/openingHours';
import { filterPlacesByRadius, sortPlacesByDistance } from './utils/distance';
import { MapToggle } from './components/common/MapToggle';
import { BottomNavBar } from './components/common/BottomNavBar';
import { InstallBanner } from './components/pwa';

// Lazy-loaded heavy components (code splitting)
// PlaceDetail and PlaceWizard are NOT lazy-loaded to fix mobile white screen issues on first load
const ModerationPanel = lazy(() => import('./components/moderation/ModerationPanel').then(m => ({ default: m.ModerationPanel })));
const AdminPanel = lazy(() => import('./components/admin').then(m => ({ default: m.AdminPanel })));
const MapView = lazy(() => import('./components/map/MapView').then(m => ({ default: m.MapView })));
// Direct imports (not lazy) to fix mobile loading issues
import { PlaceDetail } from './components/places/PlaceDetail';
import { PlaceWizard } from './components/places/PlaceWizard';
import { Logbook } from './components/logbook';
import { MyPlaces } from './components/places/MyPlaces';
import { SharePlaceDialog } from './components/places/SharePlaceDialog';
import { ValouFeedbackToast } from './components/feedback/ValouFeedbackToast';
import type { ValouAreaId } from './types/valou';

// Lazy loading fallback component
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <LoadingSpinner size="lg" />
  </div>
);

function GoodPlaceMatchInner({
  standalone = true,
  onNavigateBack,
  user: externalUser,
  showAppNotification: externalNotification,
  initialView = 'search',
  initialPlaceId,
}: GoodPlaceMatchProps) {
  // Use external user if provided, otherwise use internal auth
  const internalAuth = useAuth();
  const user = standalone ? internalAuth.user : externalUser;
  const authLoading = standalone ? internalAuth.loading : false;

  // Realm context for GoodLife/GoodWork filtering
  const { realm } = useRealm();

  // View State
  const [view, setView] = useState<PlaceMatchView>(initialView);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);

  // Messaging State
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedChatUserId, setSelectedChatUserId] = useState<string | null>(null);
  const [selectedChatUserName, setSelectedChatUserName] = useState<string>('');
  const [selectedChatUserPhoto, setSelectedChatUserPhoto] = useState<string | null>(null);

  // Groups State
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Share Place State
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [placeToShare, setPlaceToShare] = useState<Place | null>(null);

  // Article State
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Data State
  const [places, setPlaces] = useState<Place[]>([]);
  const [filteredPlaces, setFilteredPlaces] = useState<Place[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter State
  const [filters, setFilters] = useState<PlaceFilter>(INITIAL_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [activeCategory, setActiveCategory] = useState<AnyCategory | ''>('');
  const [activeVibeTags, setActiveVibeTags] = useState<VibeTag[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Mood Discovery State
  const [showMoodSelector, setShowMoodSelector] = useState(true);
  const [selectedMood, setSelectedMood] = useState<MoodType | null>(null);
  const [moodKeywords, setMoodKeywords] = useState<string[]>([]);

  // Quick Filters State
  const [activeQuickFilters, setActiveQuickFilters] = useState<QuickFilterType[]>([]);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Location State
  const { location: userLocation, status: locationStatus, requestLocation, address: userAddress, displayName: userLocationName, isLoading: isLocating } = useUserLocation();
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [searchLocation, setSearchLocation] = useState<GeocodedLocation | null>(null);
  const [searchRadius, setSearchRadius] = useState(5); // km

  // Moderation State
  const [isModerator, setIsModerator] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Auth Modal State
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');

  // Valou Feedback State (fuer Micro-Umfrage nach Favorit)
  const [showValouFeedback, setShowValouFeedback] = useState(false);
  const [feedbackPlaceId, setFeedbackPlaceId] = useState<string | null>(null);
  const [feedbackPlaceName, setFeedbackPlaceName] = useState<string>('');

  // Toast notifications
  const { toasts, showToast, removeToast } = useToast();

  // Welcome screen for first-time visitors
  const { showWelcome, closeWelcome } = useWelcomeScreen();

  // Notification handler
  const showNotification = useCallback(
    (message: string, type: 'success' | 'info' | 'error' = 'info') => {
      if (externalNotification) {
        externalNotification(message, type);
      } else {
        // Use internal toast for standalone mode
        showToast(message, type);
      }
    },
    [externalNotification, showToast]
  );

  // Load initial data - pass user ID for visibility filtering
  useEffect(() => {
    const unsubscribePlaces = subscribeToPlaces((loadedPlaces) => {
      setPlaces(loadedPlaces);
      setIsLoading(false);
    }, user?.uid);

    return () => unsubscribePlaces();
  }, [user?.uid]);

  // Clear category and vibe tags when realm changes
  // (categories/tags are realm-specific and won't match after switching)
  useEffect(() => {
    setActiveCategory('');
    setActiveVibeTags([]);
  }, [realm]);

  // Load favorites when user is available
  useEffect(() => {
    if (!user?.uid) {
      setFavorites([]);
      return;
    }

    const unsubscribeFavorites = subscribeToFavorites(user.uid, (placeIds) => {
      setFavorites(placeIds);
    });

    return () => unsubscribeFavorites();
  }, [user?.uid]);

  // Check moderator and admin status
  useEffect(() => {
    async function checkRoles() {
      if (!user?.uid) {
        setIsModerator(false);
        setIsAdmin(false);
        setPendingCount(0);
        return;
      }

      try {
        const [moderator, admin, count] = await Promise.all([
          isUserModerator(user.uid),
          isUserAdmin(user.uid),
          getPendingPlacesCount(),
        ]);
        setIsModerator(moderator);
        setIsAdmin(admin);
        setPendingCount(count);
      } catch (error) {
        console.error('Error checking roles:', error);
      }
    }

    checkRoles();
  }, [user?.uid]);

  // Filter places by category, search query, mood, quick filters, and advanced filters
  useEffect(() => {
    let result = places;

    // Filter by realm (GoodLife or GoodWork)
    // Places without realms are treated as ['goodlife'] (default)
    result = result.filter((place) => {
      const placeRealms = place.realms || ['goodlife'];
      return placeRealms.includes(realm);
    });

    // Filter by category (realm-specific)
    // GoodLife uses 'category', GoodWork uses 'goodworkCategory'
    if (activeCategory) {
      result = result.filter((place) => {
        if (realm === 'goodwork') {
          return place.goodworkCategory === activeCategory;
        }
        return place.category === activeCategory;
      });
    }

    // Filter by vibe tags (mindestens ein ausgewaehlter Tag muss vorhanden sein)
    // GoodLife uses 'vibeTags', GoodWork uses 'goodworkVibeTags'
    if (activeVibeTags.length > 0) {
      result = result.filter((place) => {
        const placeTags: string[] = realm === 'goodwork'
          ? (place.goodworkVibeTags || [])
          : (place.vibeTags || []);
        return activeVibeTags.some((tag) => placeTags.includes(tag));
      });
    }

    // Filter by search query (Was & wofuer?)
    if (searchQuery || filters.searchWhat) {
      const rawQuery = (searchQuery || filters.searchWhat).toLowerCase().trim();

      // Normalisiere Umlaute fuer bessere Suche (beide Varianten zu ASCII)
      const normalizeText = (text: string): string => {
        return text
          .toLowerCase()
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss');
      };

      const queryNorm = normalizeText(rawQuery);

      result = result.filter((place) => {
        // Textfelder durchsuchen (normalisiert und original)
        const searchableText = [
          place.name,
          place.tagline,
          place.description,
          place.goodFactor,
          place.pitch,
          place.city,
          place.district,
          place.address,
          place.category,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        // Array-Felder durchsuchen
        const arrayText = [
          ...(place.moods || []),
          ...(place.activities || []),
          ...(place.community || []),
          ...(place.vibeTags || []),
        ]
          .join(' ')
          .toLowerCase();

        const allText = searchableText + ' ' + arrayText;
        const allTextNorm = normalizeText(allText);

        // Suche in Original und normalisierter Version
        return allText.includes(rawQuery) || allTextNorm.includes(queryNorm);
      });
    }

    // Filter by location search (Wo suchst Du?)
    // AUCH Namen durchsuchen - damit z.B. "Kabinettsgarten" gefunden wird
    if (filters.searchWhere && filters.searchWhere.trim() && filters.searchWhere !== 'Aktueller Standort') {
      // Normalisiere Suchbegriff (Umlaute zu ASCII fuer konsistente Suche)
      const normalizeText = (text: string): string => {
        return text
          .toLowerCase()
          .trim()
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss');
      };

      const locationSearch = normalizeText(filters.searchWhere);
      const locationSearchOriginal = filters.searchWhere.toLowerCase().trim();

      result = result.filter((place) => {
        // AUCH Name durchsuchen - wichtig fuer konkrete Place-Namen!
        const nameNorm = normalizeText(place.name || '');
        const nameMatch = nameNorm.includes(locationSearch) ||
                          place.name?.toLowerCase().includes(locationSearchOriginal);

        const cityNorm = normalizeText(place.city || '');
        const districtNorm = normalizeText(place.district || '');
        const addressNorm = normalizeText(place.address || '');

        const cityMatch = cityNorm.includes(locationSearch) ||
                          place.city?.toLowerCase().includes(locationSearchOriginal);
        const districtMatch = districtNorm.includes(locationSearch) ||
                              place.district?.toLowerCase().includes(locationSearchOriginal);
        const addressMatch = addressNorm.includes(locationSearch) ||
                             place.address?.toLowerCase().includes(locationSearchOriginal);
        const postalMatch = place.postalCode?.toLowerCase().startsWith(locationSearchOriginal);

        // Koordinaten-Match (50km Radius)
        let coordMatch = false;
        if (searchLocation && searchLocation.latitude !== 0 && searchLocation.longitude !== 0 && place.latitude && place.longitude) {
          const distance = Math.sqrt(
            Math.pow((place.latitude - searchLocation.latitude) * 111, 2) +
            Math.pow((place.longitude - searchLocation.longitude) * 111 * Math.cos(searchLocation.latitude * Math.PI / 180), 2)
          );
          coordMatch = distance <= 50;
        }

        return nameMatch || cityMatch || districtMatch || addressMatch || postalMatch || coordMatch;
      });
    }

    // Filter by mood keywords (from MoodSelector)
    if (moodKeywords.length > 0) {
      result = result.filter((place) =>
        moodKeywords.some(
          (keyword) =>
            place.moods.some((m) => m.toLowerCase().includes(keyword.toLowerCase())) ||
            place.activities.some((a) => a.toLowerCase().includes(keyword.toLowerCase()))
        )
      );
    }

    // Apply quick filters
    for (const filterId of activeQuickFilters) {
      const filterCriteria = getFilterCriteria(filterId);

      if (filterId === 'open_now') {
        result = result.filter((place) => {
          const isOpen = isPlaceOpenNow(place.openingHours);
          return isOpen === true || isOpen === null; // Include unknown
        });
      } else if (filterId === 'nearby') {
        // Use searchLocation if set, otherwise userLocation
        const centerLocation = searchLocation || (userLocation ? { latitude: userLocation.latitude, longitude: userLocation.longitude } : null);
        if (centerLocation) {
          result = filterPlacesByRadius(result, centerLocation, searchRadius);
        }
      } else if (filterCriteria) {
        // Apply criteria-based filters
        if (filterCriteria.categories?.length) {
          result = result.filter((place) =>
            filterCriteria.categories!.includes(place.category)
          );
        }
        if (filterCriteria.moods?.length) {
          result = result.filter((place) =>
            filterCriteria.moods!.some((m) =>
              place.moods.some((pm) => pm.toLowerCase().includes(m.toLowerCase()))
            )
          );
        }
        if (filterCriteria.activities?.length) {
          result = result.filter((place) =>
            filterCriteria.activities!.some((a) =>
              place.activities.some((pa) => pa.toLowerCase().includes(a.toLowerCase()))
            )
          );
        }
        if (filterCriteria.community?.length) {
          result = result.filter((place) =>
            filterCriteria.community!.some((c) =>
              place.community.some((pc) => pc.toLowerCase().includes(c.toLowerCase()))
            )
          );
        }
      }
    }

    // Apply advanced filters
    if (filters.moods.length > 0) {
      result = result.filter((place) =>
        filters.moods.some((mood) => place.moods.includes(mood))
      );
    }

    if (filters.activities.length > 0) {
      result = result.filter((place) =>
        filters.activities.some((activity) => place.activities.includes(activity))
      );
    }

    if (filters.community.length > 0) {
      result = result.filter((place) =>
        filters.community.some((feature) => place.community.includes(feature))
      );
    }

    // Sort by distance if:
    // 1. nearby filter is active
    // 2. "Aktueller Standort" is selected in searchWhere
    // 3. a searchLocation is set (manual location selection)
    const shouldSortByDistance =
      activeQuickFilters.includes('nearby') ||
      filters.searchWhere === 'Aktueller Standort' ||
      searchLocation !== null;

    if (shouldSortByDistance) {
      const centerLocation = searchLocation || (userLocation ? { latitude: userLocation.latitude, longitude: userLocation.longitude } : null);
      if (centerLocation) {
        result = sortPlacesByDistance(result, centerLocation);
      }
    }

    setFilteredPlaces(result);
  }, [places, realm, activeCategory, activeVibeTags, searchQuery, filters, moodKeywords, activeQuickFilters, userLocation, searchLocation, searchRadius]);

  // Count active filters
  const activeFilterCount =
    filters.moods.length +
    filters.activities.length +
    filters.community.length;

  // Auth handlers
  const openAuthModal = useCallback((mode: 'login' | 'register' = 'login') => {
    // In embedded mode, don't show auth modal - notify parent app instead
    if (!standalone) {
      showNotification('Bitte melde Dich in der Haupt-App an, um diese Funktion zu nutzen.', 'info');
      return;
    }
    setAuthModalMode(mode);
    setShowAuthModal(true);
  }, [standalone, showNotification]);

  // Navigation
  const navigateToView = useCallback((newView: PlaceMatchView, place?: Place) => {
    // Require auth for create view
    if ((newView === 'create' || newView === 'createIntro') && !user?.uid) {
      openAuthModal('login');
      return;
    }

    // Redirect 'create' to 'createIntro' first (motivierende Einfuehrung)
    if (newView === 'create') {
      setView('createIntro');
      window.scrollTo(0, 0);
      return;
    }

    setView(newView);
    if (place) {
      setSelectedPlace(place);
    }
    window.scrollTo(0, 0);
  }, [user?.uid, openAuthModal]);

  const handleBack = useCallback(() => {
    if (view === 'detail') {
      setView('search');
      setSelectedPlace(null);
    } else if (onNavigateBack) {
      onNavigateBack();
    } else {
      setView('search');
    }
  }, [view, onNavigateBack]);

  // Favorite toggle
  const handleToggleFavorite = useCallback(
    async (placeId: string) => {
      if (!user?.uid) {
        openAuthModal('login');
        return;
      }

      try {
        const isNowFavorite = await toggleFavorite(user.uid, placeId);

        if (isNowFavorite) {
          // Pruefen ob User bereits Feedback fuer diesen Ort gegeben hat
          const hasFeedback = await hasUserFeedbackForPlace(placeId, user.uid, 'favorite');

          if (!hasFeedback) {
            // Ort-Namen finden fuer Feedback-Toast
            const place = places.find((p) => p.id === placeId);
            if (place) {
              setFeedbackPlaceId(placeId);
              setFeedbackPlaceName(place.name);
              setShowValouFeedback(true);
            } else {
              // Kein Place gefunden, normaler Toast
              showNotification('Zu Favoriten hinzugefuegt', 'success');
            }
          } else {
            // User hat bereits Feedback gegeben, normaler Toast
            showNotification('Zu Favoriten hinzugefuegt', 'success');
          }
        } else {
          // Favorit entfernt
          showNotification('Aus Favoriten entfernt', 'success');
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
        showNotification('Fehler beim Speichern', 'error');
      }
    },
    [user?.uid, showNotification, openAuthModal, places]
  );

  // Valou Feedback Handler
  const handleValouFeedbackSubmit = useCallback(
    async (areas: ValouAreaId[]) => {
      if (!user?.uid || !feedbackPlaceId) return;

      try {
        await saveValouFeedback(feedbackPlaceId, user.uid, areas, 'favorite');
        // Optional: Erfolgs-Toast anzeigen (deaktiviert, da subtil sein soll)
        // showNotification('Danke fuer dein Feedback!', 'success');
      } catch (error) {
        console.error('Error saving valou feedback:', error);
      }
    },
    [user?.uid, feedbackPlaceId]
  );

  const handleValouFeedbackDismiss = useCallback(() => {
    setShowValouFeedback(false);
    setFeedbackPlaceId(null);
    setFeedbackPlaceName('');
  }, []);

  // Create place
  const handleCreatePlace = useCallback(
    async (data: PlaceCreateInput) => {
      if (!user?.uid) {
        openAuthModal('login');
        throw new Error('Nicht angemeldet');
      }

      try {
        await createPlace(data, user.uid);
        // Show appropriate message based on visibility
        const message = data.visibility === 'private'
          ? 'Privater Ort erstellt! Du findest ihn unter "Meine Orte".'
          : 'Ort eingereicht! Er wird nach der Moderation veroeffentlicht.';
        showNotification(message, 'success');
        setView('search');
      } catch (error) {
        console.error('Error creating place:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
        showNotification(`Fehler beim Einreichen: ${errorMessage}`, 'error');
        throw error;
      }
    },
    [user?.uid, showNotification, openAuthModal]
  );

  // Filter handlers
  const handleFiltersChange = useCallback((updates: Partial<PlaceFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setActiveCategory('');
    setActiveVibeTags([]);
    setSearchQuery('');
    setMoodKeywords([]);
    setSelectedMood(null);
    setShowMoodSelector(true);
    setActiveQuickFilters([]);
    setSearchLocation(null);
    // Scroll back to top to show Hero
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Quick filter handlers
  const handleQuickFilterToggle = useCallback((filterId: QuickFilterType) => {
    // Request location when nearby filter is clicked
    if (filterId === 'nearby' && locationStatus !== 'granted' && !searchLocation) {
      requestLocation();
    }

    setActiveQuickFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((f) => f !== filterId)
        : [...prev, filterId]
    );
  }, [locationStatus, requestLocation, searchLocation]);

  // Location handlers
  const handleLocationSelect = useCallback((location: GeocodedLocation) => {
    setSearchLocation(location);
    const displayName = location.district || location.city || location.formattedAddress;
    setFilters(prev => ({ ...prev, searchWhere: displayName, searchLocation: location }));
  }, []);

  const handleNearbyClick = useCallback(() => {
    requestLocation();
    // When using current location, clear manual search location
    setSearchLocation(null);
    // Auto-enable nearby filter
    if (!activeQuickFilters.includes('nearby')) {
      setActiveQuickFilters((prev) => [...prev, 'nearby']);
    }
  }, [requestLocation, activeQuickFilters]);

  // View mode toggle
  const handleToggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === 'grid' ? 'map' : 'grid'));
  }, []);

  const handleClearQuickFilters = useCallback(() => {
    setActiveQuickFilters([]);
  }, []);

  // Mood selection handler
  const handleMoodSelect = useCallback((mood: MoodType, keywords: string[]) => {
    setSelectedMood(mood);
    setShowMoodSelector(false);

    // For "surprise" mood, pick random places
    if (mood === 'surprise') {
      setMoodKeywords([]);
    } else {
      setMoodKeywords(keywords);
    }
  }, []);

  const handleMoodSkip = useCallback(() => {
    setShowMoodSelector(false);
    setMoodKeywords([]);
  }, []);

  // Category change handler
  const handleCategoryChange = useCallback((category: AnyCategory | '') => {
    setActiveCategory(category);
  }, []);

  // Vibe tag handlers
  const handleVibeTagToggle = useCallback((tag: VibeTag) => {
    setActiveVibeTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  const handleClearVibeTags = useCallback(() => {
    setActiveVibeTags([]);
  }, []);

  // Search handler - reset conflicting filters when searching
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    // Reset mood keywords to avoid conflicting filters
    if (query) {
      setMoodKeywords([]);
      setSelectedMood(null);
    }
  }, []);

  // Loading state
  if (authLoading || isLoading) {
    return <LoadingScreen message="Gute Orte werden geladen..." />;
  }

  // Render PlaceDetail (not lazy-loaded to fix mobile white screen)
  if (view === 'detail' && selectedPlace) {
    return (
      <PlaceDetail
        place={selectedPlace}
        isFavorite={favorites.includes(selectedPlace.id)}
        userId={user?.uid}
        userLocation={userLocation}
        onBack={handleBack}
        onToggleFavorite={handleToggleFavorite}
        onShowNotification={showNotification}
      />
    );
  }

  // Render PlaceWizardIntro (motivierende Einfuehrung vor dem Formular)
  if (view === 'createIntro') {
    return (
      <PlaceWizardIntro
        onContinue={() => setView('create')}
        onCancel={() => setView('search')}
      />
    );
  }

  // Render PlaceWizard (not lazy-loaded to fix mobile white screen issues)
  if (view === 'create') {
    return (
      <PlaceWizard
        onSubmit={handleCreatePlace}
        onCancel={() => setView('search')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white goodplace-match pb-20 md:pb-0">
      {/* Fixed Header */}
      <Header
        onNavigate={navigateToView}
        onSearch={handleSearch}
        searchQuery={searchQuery}
        isModerator={isModerator}
        isAdmin={isAdmin}
        favoritesCount={favorites.length}
        pendingCount={pendingCount}
        onAuthRequest={openAuthModal}
        onMobileSearchClick={() => setShowMobileSearch(true)}
        selectedLocation={filters.searchWhere || undefined}
        onLocationSelect={handleLocationSelect}
        onNearbyClick={handleNearbyClick}
        isLocating={isLocating}
        nearbyAddress={userLocationName}
      />

      {/* Filter Bars - sticky container (only on search view) */}
      {view === 'search' && (
        <div className="sticky top-20 z-30 bg-white shadow-sm">
          <CategoryBar
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
          <VibeTagFilter
            selectedTags={activeVibeTags}
            onTagToggle={handleVibeTagToggle}
            selectedGoodworkTags={activeVibeTags as any}
            onGoodworkTagToggle={handleVibeTagToggle as any}
            onClearAll={handleClearVibeTags}
          />
        </div>
      )}

      {/* Main Content - with padding for fixed header */}
      <main className={`${view === 'search' ? 'pt-0' : 'pt-24'}`}>
        {view === 'search' && (
          <>
            {/* Hero Section - Atmosphaerischer Einstieg (nur wenn KEINE Suche aktiv) */}
            {showMoodSelector && !searchQuery && !filters.searchWhere && !filters.searchWhat && !activeCategory && activeVibeTags.length === 0 && activeFilterCount === 0 && (
              <HeroSection
                userName={user?.displayName || undefined}
                onMoodSelect={(mood: HeroMoodType) => {
                  // Map mood to keywords for filtering
                  const keywordsMap: Record<HeroMoodType, string[]> = {
                    // GoodLife moods
                    calm: ['ruhig', 'entspannt', 'still', 'meditation'],
                    cozy: ['gemuetlich', 'warm', 'vertraut', 'wohnzimmer'],
                    creative: ['kreativ', 'inspiration', 'kunst', 'atelier'],
                    lively: ['lebendig', 'gesellig', 'action', 'menschen'],
                    surprise: [],
                    // GoodWork moods
                    fokus: ['fokus', 'konzentriert', 'ruhig', 'ungestoert'],
                    austausch: ['austausch', 'zusammen', 'team', 'meeting', 'netzwerk'],
                    inspiration: ['kreativ', 'ideen', 'inspiration', 'studio'],
                    werkstatt: ['werkstatt', 'handwerk', 'maker', 'bauen', 'erschaffen'],
                    wissen: ['lernen', 'wissen', 'bildung', 'bibliothek', 'weiterbildung'],
                  };
                  handleMoodSelect(mood as MoodType, keywordsMap[mood] || []);
                }}
                onSkip={handleMoodSkip}
                onScrollDown={() => {
                  handleMoodSkip();
                  window.scrollTo({ top: window.innerHeight * 0.5, behavior: 'smooth' });
                }}
              />
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {/* Concierge Banner - AI-gestuetzte Suche */}
              <ConciergeBanner
                onClick={() => setView('concierge')}
                realm={realm}
              />

              {/* Mood Active Indicator */}
              {!showMoodSelector && selectedMood && (
                <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl px-4 py-3 border border-amber-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Deine Auswahl:</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-amber-700 shadow-sm border border-amber-200">
                      {/* GoodLife Moods */}
                      {selectedMood === 'calm' && '🌿 Ruhe'}
                      {selectedMood === 'cozy' && '☕ Gemuetlich'}
                      {selectedMood === 'social' && '👥 Menschen'}
                      {selectedMood === 'creative' && '🎨 Inspiration'}
                      {selectedMood === 'lively' && '✨ Lebendig'}
                      {selectedMood === 'surprise' && '🎲 Zufall'}
                      {/* GoodWork Moods */}
                      {selectedMood === 'fokus' && 'Fokus'}
                      {selectedMood === 'austausch' && 'Austausch'}
                      {selectedMood === 'inspiration' && 'Inspiration'}
                      {selectedMood === 'werkstatt' && 'Werkstatt'}
                      {selectedMood === 'wissen' && 'Wissen'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setShowMoodSelector(true);
                      setSelectedMood(null);
                      setMoodKeywords([]);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="text-sm text-amber-700 hover:text-amber-900 hover:underline transition-colors"
                  >
                    Zurueck zur Startseite
                  </button>
                </div>
              )}

              {/* Quick Filters - Horizontal chips */}
              {!showMoodSelector && (
                <QuickFilters
                  activeFilters={activeQuickFilters}
                  onFilterToggle={handleQuickFilterToggle}
                  onClearAll={handleClearQuickFilters}
                  isOpenNowAvailable={true}
                  isNearbyAvailable={locationStatus === 'granted'}
                />
              )}

            {/* Suchergebnisse ODER Homepage-Sektionen */}
            {activeFilterCount > 0 || activeCategory || activeVibeTags.length > 0 || filters.searchWhere || filters.searchWhat || searchQuery ? (
              /* Filtered Results - NUR Suchergebnisse wenn Filter aktiv */
              <>
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {filteredPlaces.length} {filteredPlaces.length === 1 ? 'Ort' : 'Orte'} gefunden
                    </h2>
                    {(filters.searchWhere || activeCategory) && (
                      <p className="text-sm text-gray-500">
                        {filters.searchWhere ? `in ${filters.searchWhere}` : ''}
                        {filters.searchWhere && activeCategory ? ' - ' : ''}
                        {activeCategory ? (activeCategory === 'urban_creative' ? 'Urbane Oasen & Kreatives' :
                            activeCategory === 'public_living_room' ? 'Öffentliche Wohnzimmer' :
                            activeCategory === 'analoge_refugien' ? 'Analoge Refugien' :
                            activeCategory === 'silent_sanctuary' ? 'Stille & Rückzug' :
                            activeCategory === 'natur_outdoor' ? 'Natur & Outdoor' : activeCategory) : ''}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleResetFilters}
                    className="text-sm text-amber-700 hover:text-amber-900 hover:underline transition-colors"
                  >
                    Filter zuruecksetzen
                  </button>
                </div>

                {/* Filtered Places - Grid or Map */}
                {viewMode === 'grid' ? (
                  <PlaceGrid
                    places={filteredPlaces}
                    favorites={favorites}
                    loading={false}
                    onSelect={(place) => navigateToView('detail', place)}
                    onToggleFavorite={handleToggleFavorite}
                    userLocation={userLocation}
                  />
                ) : (
                  <Suspense fallback={<LazyFallback />}>
                    <MapView
                      places={filteredPlaces}
                      center={searchLocation || userLocation || { latitude: 48.1351, longitude: 11.5820 }}
                      onPlaceSelect={(place) => navigateToView('detail', place)}
                      userLocation={userLocation}
                      className="h-[70vh] rounded-xl"
                    />
                  </Suspense>
                )}
              </>
            ) : (
              /* Homepage - alle Sektionen wenn KEINE Filter aktiv */
              <>
                {/* Onboarding Banner */}
                <OnboardingBanner onSuggestPlace={() => navigateToView('create')} />

                {/* Featured Content / Articles */}
                <FeaturedContent
                  onArticleClick={(article) => {
                    setSelectedArticle(article);
                    setView('articleDetail');
                  }}
                />

                {/* Inspiration Section */}
                <InspirationSection
                  favorites={favorites}
                  onSelectPlace={(place) => navigateToView('detail', place)}
                  onToggleFavorite={handleToggleFavorite}
                />

                {/* Curated Homepage */}
                <CuratedHomepage
                  places={filteredPlaces}
                  favorites={favorites}
                  onSelectPlace={(place) => navigateToView('detail', place)}
                  onToggleFavorite={handleToggleFavorite}
                  selectedMood={selectedMood}
                  activeCategory={activeCategory}
                />
              </>
            )}
            </div>
          </>
        )}

        {view === 'favorites' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Meine Lieblingsorte</h2>
            <FavoritesList
              favoriteIds={favorites}
              onSelectPlace={(place) => navigateToView('detail', place)}
              onToggleFavorite={handleToggleFavorite}
            />
          </div>
        )}

        {view === 'moderation' && user?.uid && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Moderation</h2>
            <Suspense fallback={<LazyFallback />}>
              <ModerationPanel
                userId={user.uid}
                onShowNotification={showNotification}
              />
            </Suspense>
          </div>
        )}

        {view === 'admin' && user?.uid && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Suspense fallback={<LazyFallback />}>
              <AdminPanel
                userId={user.uid}
                onShowNotification={showNotification}
              />
            </Suspense>
          </div>
        )}

        {view === 'logbook' && user?.uid && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <ErrorBoundary
              onError={(error) => {
                console.error('Logbook error:', error);
                showNotification('Fehler beim Laden des Logbooks', 'error');
              }}
            >
              <Logbook
                userId={user.uid}
                places={places}
                onNavigateToPlace={(placeId) => {
                  const place = places.find((p) => p.id === placeId);
                  if (place) {
                    navigateToView('detail', place);
                  }
                }}
                onShowNotification={showNotification}
              />
            </ErrorBoundary>
          </div>
        )}

        {view === 'myPlaces' && user?.uid && (
          <MyPlaces
            userId={user.uid}
            onBack={() => setView('search')}
            onViewPlace={(placeId) => {
              const place = places.find((p) => p.id === placeId);
              if (place) {
                setSelectedPlace(place);
                setView('detail');
              }
            }}
            onCreate={() => navigateToView('create')}
            onSharePlace={(place) => {
              setPlaceToShare(place);
              setShowShareDialog(true);
            }}
            showNotification={showNotification}
          />
        )}

        {view === 'community' && (
          <CommunityHub
            onBack={() => setView('search')}
            onViewProfile={(userId) => {
              setSelectedProfileUserId(userId);
              setView('publicProfile');
            }}
            onViewPlace={(placeId) => {
              const place = places.find(p => p.id === placeId);
              if (place) {
                setSelectedPlace(place);
                setView('detail');
              }
            }}
            onViewGroup={(groupId) => {
              setSelectedGroupId(groupId);
              setView('groupDetail');
            }}
            onExploreGroups={() => setView('groups')}
            onSearchUsers={() => setView('userSearch')}
            onViewMessages={() => setView('messages')}
            onViewEvents={() => setView('communityEvents')}
          />
        )}

        {view === 'communityEvents' && (
          <CommunityEvents
            onBack={() => setView('community')}
            onViewGroup={(groupId) => {
              setSelectedGroupId(groupId);
              setView('groupDetail');
            }}
            onViewPlace={(placeId) => {
              const place = places.find(p => p.id === placeId);
              if (place) {
                setSelectedPlace(place);
                setView('detail');
              }
            }}
          />
        )}

        {view === 'publicProfile' && selectedProfileUserId && (
          <PublicProfile
            userId={selectedProfileUserId}
            onBack={() => setView('community')}
            onViewPlace={(placeId) => {
              const place = places.find((p) => p.id === placeId);
              if (place) {
                navigateToView('detail', place);
              }
            }}
            onSendMessage={async (otherUserId, otherUserName, otherUserPhoto) => {
              if (!user?.uid) {
                openAuthModal('login');
                return;
              }
              try {
                const conversationId = await getOrCreateConversation(
                  user.uid,
                  otherUserId,
                  { name: user.displayName || 'Unbekannt', photoURL: user.photoURL || null },
                  { name: otherUserName, photoURL: otherUserPhoto }
                );
                setSelectedConversationId(conversationId);
                setSelectedChatUserId(otherUserId);
                setSelectedChatUserName(otherUserName);
                setSelectedChatUserPhoto(otherUserPhoto);
                setView('chat');
              } catch (error) {
                console.error('Error creating conversation:', error);
                showNotification('Fehler beim Starten der Unterhaltung', 'error');
              }
            }}
          />
        )}

        {view === 'privacySettings' && (
          <PrivacySettings onBack={() => setView('search')} />
        )}

        {view === 'messages' && user?.uid && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-h-[60vh]">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Nachrichten</h2>
              </div>
              <ConversationList
                userId={user.uid}
                selectedConversationId={selectedConversationId || undefined}
                onSelectConversation={(conversationId, otherUserId) => {
                  setSelectedConversationId(conversationId);
                  setSelectedChatUserId(otherUserId);
                  setView('chat');
                }}
              />
            </div>
          </div>
        )}

        {view === 'chat' && user?.uid && selectedConversationId && selectedChatUserId && (
          <div className="max-w-4xl mx-auto h-[calc(100vh-6rem)]">
            <ChatView
              conversationId={selectedConversationId}
              userId={user.uid}
              userName={user.displayName || 'Unbekannt'}
              userPhoto={user.photoURL || null}
              otherUser={{
                id: selectedChatUserId,
                name: selectedChatUserName || 'Unbekannt',
                photoURL: selectedChatUserPhoto,
              }}
              onBack={() => setView('messages')}
              onViewProfile={(userId) => {
                setSelectedProfileUserId(userId);
                setView('publicProfile');
              }}
            />
          </div>
        )}

        {/* Concierge AI Chat */}
        {view === 'concierge' && (
          <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)]">
            <ConciergeChat
              places={places}
              realm={realm}
              userId={user?.uid}
              userName={user?.displayName || undefined}
              onBack={() => setView('search')}
              onPlaceSelect={(placeId) => {
                const place = places.find(p => p.id === placeId);
                if (place) {
                  setSelectedPlace(place);
                  setView('detail');
                }
              }}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
            />
          </div>
        )}

        {/* Groups Views */}
        {view === 'groups' && (
          <GroupsExplore
            onBack={() => setView('search')}
            onViewGroup={(groupId) => {
              setSelectedGroupId(groupId);
              setView('groupDetail');
            }}
            onCreateGroup={() => setView('createGroup')}
            onViewMyGroups={() => setView('myGroups')}
          />
        )}

        {view === 'groupDetail' && selectedGroupId && (
          <GroupDetail
            groupId={selectedGroupId}
            onBack={() => setView('groups')}
            onViewPlace={(placeId) => {
              const place = places.find(p => p.id === placeId);
              if (place) {
                setSelectedPlace(place);
                setView('detail');
              }
            }}
            onViewProfile={(userId) => {
              setSelectedProfileUserId(userId);
              setView('publicProfile');
            }}
          />
        )}

        {view === 'createGroup' && (
          <GroupCreate
            onBack={() => setView('groups')}
            onSuccess={(groupId) => {
              setSelectedGroupId(groupId);
              setView('groupDetail');
              showNotification('Gruppe erfolgreich erstellt!', 'success');
            }}
          />
        )}

        {view === 'myGroups' && (
          <MyGroups
            onBack={() => setView('search')}
            onViewGroup={(groupId) => {
              setSelectedGroupId(groupId);
              setView('groupDetail');
            }}
            onCreateGroup={() => setView('createGroup')}
            onExploreGroups={() => setView('groups')}
          />
        )}

        {view === 'userSearch' && (
          <UserSearch
            onBack={() => setView('community')}
            onSelectUser={(userId) => {
              setSelectedProfileUserId(userId);
              setView('publicProfile');
            }}
          />
        )}

        {view === 'magazine' && (
          <MagazinePage
            onBack={() => setView('search')}
            onArticleClick={(article) => {
              setSelectedArticle(article);
              setView('articleDetail');
            }}
          />
        )}

        {view === 'articleDetail' && selectedArticle && (
          <ArticleDetail
            article={selectedArticle}
            onBack={() => {
              setSelectedArticle(null);
              setView('magazine');
            }}
          />
        )}
      </main>

      {/* Map Toggle Button - Floating */}
      {view === 'search' && !showMoodSelector && (
        <div className="fixed bottom-28 md:bottom-24 left-1/2 -translate-x-1/2 z-40">
          <MapToggle
            isMapView={viewMode === 'map'}
            onToggle={handleToggleViewMode}
          />
        </div>
      )}


      {/* Footer (only on main views) */}
      {(view === 'search' || view === 'favorites') && <Footer />}

      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilters}
        filters={filters}
        filteredCount={filteredPlaces.length}
        onClose={() => setShowFilters(false)}
        onFiltersChange={handleFiltersChange}
        onReset={handleResetFilters}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Valou Feedback Toast (Micro-Umfrage nach Favorit) */}
      {showValouFeedback && feedbackPlaceId && (
        <ValouFeedbackToast
          placeName={feedbackPlaceName}
          onSubmit={handleValouFeedbackSubmit}
          onDismiss={handleValouFeedbackDismiss}
        />
      )}

      {/* Welcome Screen for first-time visitors */}
      <WelcomeScreen
        isOpen={showWelcome}
        onClose={closeWelcome}
        onExplore={() => {
          closeWelcome();
          setView('search');
        }}
        onSuggest={() => {
          closeWelcome();
          navigateToView('create');
        }}
      />

      {/* Mobile Search Modal */}
      <MobileSearchModal
        isOpen={showMobileSearch}
        onClose={() => setShowMobileSearch(false)}
        onSearch={(query) => {
          setSearchQuery(query);
          setShowMobileSearch(false);
        }}
        onLocationSelect={handleLocationSelect}
        onNearbyClick={handleNearbyClick}
        initialQuery={searchQuery}
        isLocating={isLocating}
        nearbyAddress={userLocationName}
      />

      {/* PWA Install Banner (mobile only) */}
      <InstallBanner />

      {/* Share Place Dialog */}
      {showShareDialog && placeToShare && user?.uid && (
        <SharePlaceDialog
          place={placeToShare}
          currentUserId={user.uid}
          onClose={() => {
            setShowShareDialog(false);
            setPlaceToShare(null);
          }}
          onUpdate={(updatedPlace) => {
            setPlaceToShare(updatedPlace);
            // Update in places list as well
            setPlaces((prev) =>
              prev.map((p) => (p.id === updatedPlace.id ? updatedPlace : p))
            );
          }}
          showNotification={showNotification}
        />
      )}

      {/* Bottom Navigation Bar (mobile only) */}
      {standalone && (
        <BottomNavBar
          currentView={view}
          onNavigate={navigateToView}
          isLoggedIn={!!user}
          onAuthRequired={() => openAuthModal('login')}
        />
      )}
    </div>
  );
}

// Wrapper with Context Providers
export function GoodPlaceMatch(props: GoodPlaceMatchProps) {
  const config: Partial<GoodPlaceConfig> = {
    standalone: props.standalone ?? true,
    features: {
      showAddPlace: true,
      showFavorites: true,
      showEvents: true,
      showReviews: true,
      showMap: true,
      showModeration: true,
    },
    onNotification: props.showAppNotification,
  };

  // Always include AuthProvider - Firebase auth state is shared across apps
  // using the same Firebase project (goodwork-crafting-app)
  // RealmProvider manages GoodLife/GoodWork mode selection
  return (
    <AuthProvider>
      <RealmProvider>
        <ConfigProvider config={config}>
          <GoodPlaceMatchInner {...props} />
        </ConfigProvider>
      </RealmProvider>
    </AuthProvider>
  );
}

export default GoodPlaceMatch;
